import React from "react";
import { Card, CardContent, CardHeader, Grid, Typography } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import moment from "moment";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#AF19FF",
  "#FF5733",
  "#C70039",
  "#900C3F",
  "#581845",
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "white",
          padding: "10px",
          border: "1px solid #ccc",
        }}
      >
        <p className="label">{`Date : ${label}`}</p>
        <p className="intro">{`Loan Amount : ${payload[0].value}`}</p>
      </div>
    );
  }

  return null;
};

const AdminLoanCharts = ({ loans }) => {
  if (!loans || loans.length === 0) {
    return (
      <Typography
        variant="h6"
        color="text.secondary"
        sx={{ textAlign: "center", mt: 4 }}
      >
        No loan data available to display charts.
      </Typography>
    );
  }

  // Data for Active Status Pie Chart
  const activeStatusData = loans.reduce((acc, loan) => {
    const status = loan.active_status || "Unknown";
    const existing = acc.find((item) => item.name === status);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ name: status, value: 1 });
    }
    return acc;
  }, []);

  // Data for Loan Amounts Over Time Line Chart
  const loanAmountsOverTime = loans
    .map((loan) => ({
      date: moment(loan.loan_start_date).format("YYYY-MM-DD"),
      amount: loan.loan_principal,
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  // Data for Principal vs. Interest by Product Stacked Bar Chart
  const productData = loans.reduce((acc, loan) => {
    const product = loan.product_id || "Unknown";
    const existing = acc.find((item) => item.name === product);
    if (existing) {
      existing.principal += loan.loan_principal;
      existing.interest += loan.loan_interest;
    } else {
      acc.push({
        name: product,
        principal: loan.loan_principal,
        interest: loan.loan_interest,
      });
    }
    return acc;
  }, []);

  // Data for Overall Repayment Progress Donut Chart
  const totalPrincipal = loans.reduce(
    (sum, loan) => sum + loan.loan_principal,
    0
  );
  const totalPaid = loans.reduce((sum, loan) => sum + loan.amount_paid, 0);
  const repaymentData = [
    { name: "Amount Paid", value: totalPaid },
    { name: "Remaining Principal", value: totalPrincipal - totalPaid },
  ];

  return (
    <Grid container spacing={3}>
      {/* Active Status Distribution */}
      <Grid item xs={12} md={6} lg={4}>
        <Card>
          <CardHeader title="Loan Active Status" />
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={activeStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label
                >
                  {activeStatusData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Overall Repayment Progress */}
      {/* <Grid item xs={12} md={6} lg={4}>
        <Card>
          <CardHeader title="Overall Repayment Progress" />
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={repaymentData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} fill="#8884d8" paddingAngle={5} dataKey="value" label>
                  {repaymentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid> */}

      {/* Loan Amounts Over Time */}
      <Grid item xs={12} md={12} lg={8}>
        <Card>
          <CardHeader title="Loan Amounts Over Time" />
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={loanAmountsOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#8884d8"
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Principal vs. Interest by Product */}
      <Grid item xs={12}>
        <Card>
          <CardHeader title="Principal vs. Interest by Product" />
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={productData}
                layout="vertical"
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" />
                <Tooltip />
                <Legend />
                <Bar dataKey="principal" stackId="a" fill="#8884d8" />
                <Bar dataKey="interest" stackId="a" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default AdminLoanCharts;
